import React, { useState, useRef, useEffect } from "react";
import { Phone, Mail, MapPin, Send, MessageCircle, CheckCircle, AlertCircle, Paperclip, X, Upload } from "lucide-react";
import { AnimatedSection, SectionLabel, Seo } from "../components/ui/index";
import { contactAPI, cmsAPI } from "../services/api";
import { useSettings } from "../context/SettingsContext";

const ALLOWED_TYPES = ["image/jpeg","image/jpg","image/png","image/webp","application/pdf","application/msword","application/vnd.openxmlformats-officedocument.wordprocessingml.document","text/plain"];
const ALLOWED_EXTS = [".jpg",".jpeg",".png",".webp",".pdf",".doc",".docx",".txt"];
const MAX_MB = 5;

export function ContactPage() {
  const { get } = useSettings();
  const fileInputRef = useRef();
  const [cms, setCms] = useState({});
  const [form, setForm] = useState({ name:"", phone:"", email:"", subject:"", message:"" });
  const [attachment, setAttachment] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    cmsAPI.getPage("contact").then(r => setCms(r.data.content || {})).catch(() => {});
  }, []);

  const g = (s, k, fb) => cms?.[s]?.[k]?.value || fb;

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Full name is required";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email address required";
    if (!form.message.trim() || form.message.trim().length < 10) e.message = "Please write at least 10 characters";
    return e;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setApiError(`File type not allowed. Accepted: Images, PDF, DOC, DOCX, TXT`);
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setApiError(`File too large. Maximum size is ${MAX_MB}MB.`);
      return;
    }
    setAttachment(file);
    setApiError("");
  };

  const removeAttachment = () => { setAttachment(null); if (fileInputRef.current) fileInputRef.current.value = ""; };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true); setApiError("");
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) formData.append(k, v); });
      if (attachment) formData.append("attachment", attachment);
      await contactAPI.submit(formData);
      setSubmitted(true);
    } catch (err) {
      setApiError(err.response?.data?.error || "Submission failed. Please try again or contact us directly.");
    } finally { setLoading(false); }
  };

  const formatFileSize = (bytes) => bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

  return (
    <div className="pt-20">
      <Seo page="contact" defaultTitle="Contact Us — Sava Fresh Trading FZE" />
      {/* Hero */}
      <section className="bg-gradient-to-br from-brand-pale via-white to-accent-tealLight py-20">
        <div className="container-custom">
          <AnimatedSection className="max-w-2xl">
            <SectionLabel text="Contact Us" />
            <h1 className="section-heading mb-4">{g("hero", "heading", "Let's Start a Conversation")}</h1>
            <div className="text-gray-600 leading-relaxed text-lg rich-content" dangerouslySetInnerHTML={{ __html: g("hero", "description", "Whether you need a bulk quote, product specifications, or want to explore a long-term supply partnership — our team is ready to assist. Reach out through any channel that works best for you, and we promise a prompt, professional response.") }} />
          </AnimatedSection>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-pad">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-10">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-4">
              <AnimatedSection direction="left">
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">Get in Touch</h2>
                {[
                  { icon: Phone, label: "Phone / WhatsApp", value: get("call_number"), sub: g("sidebar", "phone_sub", "Available during business hours"), href: `tel:${get("call_number")}`, color: "brand" },
                  { icon: Mail, label: "Email Address", value: get("email"), sub: g("sidebar", "email_sub", "We respond within 24 hours"), href: `mailto:${get("email")}`, color: "teal" },
                  { icon: MapPin, label: "Business Address", value: get("address"), sub: g("sidebar", "address_sub", "Free Zone, UAE"), href: null, color: "brand" },
                ].map(item => (
                  <div key={item.label} className="flex items-start gap-4 p-4 bg-white rounded-2xl shadow-card hover:shadow-hover transition-all duration-300 mb-3">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${item.color === "teal" ? "bg-accent-tealLight" : "bg-brand-pale"}`}>
                      <item.icon className={`w-5 h-5 ${item.color === "teal" ? "text-accent-teal" : "text-brand-primary"}`} />
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 font-medium mb-0.5">{item.label}</div>
                      {item.href
                        ? <a href={item.href} className="font-semibold text-gray-900 text-sm hover:text-brand-primary transition-colors">{item.value}</a>
                        : <div className="font-semibold text-gray-900 text-sm">{item.value}</div>}
                      <div className="text-xs text-gray-400 mt-0.5">{item.sub}</div>
                    </div>
                  </div>
                ))}
                {/* WhatsApp CTA */}
                <a href={`https://wa.me/${(get("whatsapp_number") || "").replace(/\D/g, "")}?text=${encodeURIComponent("Hello Sava Fresh, I would like to discuss a business enquiry.")}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-2xl text-green-700 font-semibold text-sm hover:bg-green-100 transition-colors">
                  <MessageCircle className="w-5 h-5" />
                  <div>
                    <div>Chat on WhatsApp</div>
                    <div className="text-xs text-green-600 font-normal">Instant response during business hours</div>
                  </div>
                </a>

                {/* Info box */}
                <div className="bg-brand-pale rounded-2xl p-5 border border-brand-muted mt-4">
                  <h4 className="font-heading font-semibold text-brand-primary text-sm mb-3">{g("expect", "heading", "What to expect")}</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    {[
                      g("expect", "point1", "Response within 24 business hours"),
                      g("expect", "point2", "Detailed product information & pricing"),
                      g("expect", "point3", "Custom quotes for bulk orders"),
                      g("expect", "point4", "Free samples for qualified buyers"),
                    ].filter(Boolean).map((pt, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-brand-primary flex-shrink-0 mt-0.5" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <AnimatedSection direction="right">
                <div className="bg-white rounded-3xl shadow-card p-8 border border-gray-100">
                  {submitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-brand-pale rounded-full flex items-center justify-center mx-auto mb-5">
                        <CheckCircle className="w-10 h-10 text-brand-primary" />
                      </div>
                      <h3 className="font-heading font-bold text-gray-900 text-2xl mb-2">Message Sent!</h3>
                      <p className="text-gray-500 mb-2">Thank you, <strong>{form.name}</strong>! We have received your message and will respond within 24 business hours.</p>
                      <p className="text-gray-400 text-sm mb-6">A confirmation has been sent to <strong>{form.email}</strong></p>
                      <button onClick={() => { setSubmitted(false); setForm({ name:"",phone:"",email:"",subject:"",message:"" }); setAttachment(null); }} className="btn-primary">
                        Send Another Message
                      </button>
                    </div>
                  ) : (
                    <>
                      <h3 className="font-heading font-bold text-gray-900 text-xl mb-1">Send us a Message</h3>
                      <p className="text-gray-400 text-sm mb-6">Fill in the details below. All fields marked * are required.</p>

                      {apiError && (
                        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3 mb-5 text-red-700 text-sm">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />{apiError}
                        </div>
                      )}

                      <form onSubmit={handleSubmit} noValidate className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Name *</label>
                            <input className={`input-field ${errors.name ? "border-red-400 focus:ring-red-400" : ""}`} placeholder="Your full name"
                              value={form.name} onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setErrors(er => ({ ...er, name: "" })); }} />
                            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Phone Number</label>
                            <input className="input-field" placeholder="+971 XX XXX XXXX"
                              value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email Address *</label>
                          <input type="email" className={`input-field ${errors.email ? "border-red-400 focus:ring-red-400" : ""}`} placeholder="your@company.com"
                            value={form.email} onChange={e => { setForm(f => ({ ...f, email: e.target.value })); setErrors(er => ({ ...er, email: "" })); }} />
                          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Subject</label>
                          <input className="input-field" placeholder="e.g. Bulk order inquiry, Product specifications..."
                            value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} />
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Message *</label>
                          <textarea className={`input-field resize-none ${errors.message ? "border-red-400 focus:ring-red-400" : ""}`} rows={5}
                            placeholder="Tell us about your requirements, quantity needed, destination country, and any other relevant details..."
                            value={form.message} onChange={e => { setForm(f => ({ ...f, message: e.target.value })); setErrors(er => ({ ...er, message: "" })); }} />
                          {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                          <p className="text-xs text-gray-400 mt-1">{form.message.trim().split(/\s+/).filter(Boolean).length} words</p>
                        </div>

                        {/* File Attachment */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Attachment <span className="font-normal text-gray-400">(optional — max {MAX_MB}MB)</span>
                          </label>
                          {attachment ? (
                            <div className="flex items-center gap-3 p-3 bg-brand-pale border border-brand-muted rounded-xl">
                              <Paperclip className="w-4 h-4 text-brand-primary flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-800 truncate">{attachment.name}</div>
                                <div className="text-xs text-gray-500">{formatFileSize(attachment.size)}</div>
                              </div>
                              <button type="button" onClick={removeAttachment} className="w-7 h-7 flex items-center justify-center rounded-lg bg-white hover:bg-red-50 hover:text-red-500 text-gray-400 transition-colors">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <label className="flex items-center gap-3 p-3 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-brand-primary hover:bg-brand-pale/30 transition-all group">
                              <Upload className="w-5 h-5 text-gray-400 group-hover:text-brand-primary transition-colors" />
                              <div>
                                <div className="text-sm font-medium text-gray-600 group-hover:text-brand-primary transition-colors">Click to attach a file</div>
                                <div className="text-xs text-gray-400">Images, PDF, DOC, DOCX, TXT — max {MAX_MB}MB</div>
                              </div>
                              <input ref={fileInputRef} type="file" className="hidden" accept={ALLOWED_EXTS.join(",")} onChange={handleFileSelect} />
                            </label>
                          )}
                        </div>

                        <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-4 text-base">
                          {loading
                            ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending...</>
                            : <><Send className="w-4 h-4" />Send Message</>}
                        </button>
                        <p className="text-xs text-gray-400 text-center">Your information is kept strictly confidential and will only be used to respond to your inquiry.</p>
                      </form>
                    </>
                  )}
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps Embed Section */}
      <section className="section-pad bg-gray-section border-t border-gray-100">
        <div className="container-custom">
          <AnimatedSection className="text-center mb-8">
            <SectionLabel text="Find Us" color="teal" />
            <h2 className="section-heading mb-3">Visit Our Location</h2>
            <p className="section-subheading mx-auto">
              Headquartered in Sharjah Airport International Free Zone (SAIF Zone), UAE.
            </p>
          </AnimatedSection>
          <AnimatedSection delay={100}>
            <div className="bg-white p-3 rounded-3xl shadow-card border border-gray-100 overflow-hidden">
              {(() => {
                const rawMapUrl = get("map_embed_url") || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3606.5925345719364!2d55.5147514!3d25.3211531!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5f40391d1ea7%3A0x868b449b2b512fb7!2sSharjah%20Airport%20International%20Free%20Zone%20(SAIF%20Zone)!5e0!3m2!1sen!2sae!4v1700000000000!5m2!1sen!2sae";
                const mapSrc = rawMapUrl.includes('src="') ? (rawMapUrl.match(/src="([^"]+)"/) || [])[1] || rawMapUrl : rawMapUrl;
                return (
                  <iframe
                    title="Sava Fresh Location Map"
                    src={mapSrc}
                    width="100%"
                    height="450"
                    style={{ border: 0, borderRadius: "1.25rem" }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                );
              })()}
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
